import { useMemo } from "react";
import { PackagingSelection } from "~/domains/product/components/PackagingSelection/PackagingSelection";
import {
  getCountriesListFromFifteendigit,
  getPriceInfo,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { AtcMainAreaLayout } from "~/features/addToCart/components/AddToCartProcess/shell/AtcContentLayout";
import { useAddToCartFlowContext } from "~/features/addToCart/components/AddToCartProcess/AddToCartFlowContext";
import { useFormContext } from "react-hook-form";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { type PdpSaleData } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { useSalesData } from "~/features/salesCampaign/hooks/useSalesData";
import { Alert } from "~/common/mui/Alert/Alert";
import AtcProductPriceInfo from "~/features/addToCart/components/v2/AtcProductPriceInfo/AtcProductPriceInfo";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Stack, Typography, useMediaQuery, useTheme } from "~/common/mui";
import {
  findMinOrderQuantity,
  getDefaultQuantity,
  useMOQValidation,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { isMinOrderQuantityRestrictionEnabled } from "~/features/cart/cart/utils/cartUtils";
import { useTranslation } from "react-i18next";
import { AutosuggestPackageRestrictionContext } from "~/features/PackageRestriction/AutosuggestPackageRestrictionContext";
import { usePackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { usePackagingOptions } from "~/domains/product/hooks/usePackagingOptions";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { useSimulateCartPreview } from "~/domains/cart/hooks";
import { ProductAddPreview } from "~/domains/product/components/ProductAddPreview";

interface Props {
  canSeeAddToCartDisclaimer: boolean;

  productDescriptor: ProductDescriptor;
  overQtyMessageEnabled?: boolean;
  sending: boolean;
}

export const AddToCartScreen = ({
  canSeeAddToCartDisclaimer,
  productDescriptor,
  overQtyMessageEnabled = true,
  sending,
}: Props) => {
  const { restrictionQuery } = useAddToCartFlowContext();
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<AddToCartFormDraft>();
  const { main, initialPackCodeResolved } = useAddToCartDerived();

  const quantity = watch("quantity");
  const priceReference = watch("priceReference");
  const hasSurcharge = (main.netPrice?.surcharge ?? 0) > 0;
  const tariffSurcharge = main.netPrice?.tariffSurcharge ?? false;
  const previewQuantity = Number(quantity);
  const phaseOut2Enabled = isPhaseOut2Enabled();

  const selectedPack = main.selectedPack;
  const selectedPackCode = watch("selectedPackCode");

  const salesData = useSalesData();
  const salePerPack = salesData?.salePerPack ?? {};

  const restrictedCode = usePackageRestrictionContext();
  const packagingOptionsState = usePackagingOptions({
    descriptor: productDescriptor,
    priceReference,
    salePerPack,
    restrictionPackCode: restrictedCode ?? undefined,
    t,
  });

  const packSize = main.selectedPackaging?.packSize ?? selectedPack?.packSize ?? 1;

  const onSelectPackage = (packCode: string) => setValue("selectedPackCode", packCode);

  const priceInfo = getPriceInfo({ product: productDescriptor, packCode: selectedPackCode });

  const showBetterDeal =
    overQtyMessageEnabled &&
    priceInfo &&
    priceInfo.minQtyAvailableScale !== null &&
    main.totalQuantity >= priceInfo.minQtyAvailableScale;

  const minOrderQuantity = findMinOrderQuantity(productDescriptor, selectedPackCode);

  const salesHelper = useSalesHelper(productDescriptor);
  const hasSales = salesHelper.isProductOnSale();

  const { isPackageSizeInvalid, MOQHelperLabel } = useMOQValidation({
    quantity,
    packSize,
    minOrderQuantity,
    isAvailabilityLoaded: !!productDescriptor.availability,
    isPackageSelected: !!selectedPackCode,
    isOnSale: hasSales,
  });

  const isMOQCheckEnabled = isMinOrderQuantityRestrictionEnabled() && !hasSales;

  const saleForPack: PdpSaleData | undefined = salePerPack[selectedPackCode];
  const saleActive = !!saleForPack && watch("salePriceEnabled");
  const exceedsSalesStock = !!saleForPack && quantity > saleForPack.salesStock;

  const saleStockMessage =
    saleActive && exceedsSalesStock ? (
      <Alert severity={"info"}>{t("feature.salesCampaign.exceedsQty")}</Alert>
    ) : undefined;

  const minQtyValue = isMOQCheckEnabled ? getDefaultQuantity(packSize, minOrderQuantity) : packSize;

  const qtyHelperLabel = isMOQCheckEnabled ? MOQHelperLabel : undefined;

  const simulateEntries = useMemo(
    () =>
      phaseOut2Enabled && selectedPackCode && Number.isFinite(previewQuantity) && previewQuantity > 0
        ? [
            {
              productCode: productDescriptor.base.code,
              packageCode: selectedPackCode,
              packageSize: packSize,
              quantity: previewQuantity,
            },
          ]
        : [],
    [packSize, phaseOut2Enabled, previewQuantity, productDescriptor.base.code, selectedPackCode],
  );

  const { data: previewData } = useSimulateCartPreview({
    entries: simulateEntries,
    enabled: simulateEntries.length > 0,
  });

  return (
    <AutosuggestPackageRestrictionContext productCode={productDescriptor.base.code} query={restrictionQuery}>
      <AtcMainAreaLayout
        packagingSlot={
          <Stack gap={2}>
            {isPackageSizeInvalid && <Alert severity="error">{t("addToCartPopup.mismatchedQty")}</Alert>}
            {showBetterDeal && <Alert severity="info">{t("addProduct.scalingDeal")}</Alert>}

            <Typography variant="titleMedium">{t("addToCartPopup.select.packaging.title")}</Typography>
            <AddToCartPackagingSelection
              initialPackCodeResolved={initialPackCodeResolved}
              selectedPackCode={selectedPackCode}
              onSelectPackage={onSelectPackage}
              packagingOptionsState={packagingOptionsState}
            />
          </Stack>
        }
        pricingSlot={
          <Stack gap={2}>
            <AtcProductPriceInfo
              quantity={quantity}
              onChange={(newQty) => setValue("quantity", newQty, { shouldDirty: true, shouldTouch: true })}
              invalidQuantity={isPackageSizeInvalid ?? undefined}
              priceReference={priceReference}
              onPriceReferenceChange={(newVal) => setValue("priceReference", newVal ?? "")}
              descriptor={productDescriptor}
              showInfoOrderHandlingUnitQty={true}
              disabled={sending}
              helperLabel={qtyHelperLabel}
              minQtyValue={minQtyValue}
            />
            {ProductAddPreview.isVisible(productDescriptor.base.designation, selectedPackCode, previewData) && (
              <ProductAddPreview data={previewData} />
            )}
          </Stack>
        }
        notificationsSlot={
          <Stack gap={1}>
            {saleStockMessage}
            {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}
            {canSeeAddToCartDisclaimer && (
              <Alert severity="info" className="w-100">
                {t("addToCartPopup.disclaimer")}
              </Alert>
            )}
            {productDescriptor.base.isOptime && <OptimeSolutionsMessage />}
            {getCountriesListFromFifteendigit({ descriptor: productDescriptor }).length > 0 && (
              <DisclaimerCountryOrigin />
            )}
            <IncludesSurchargeDisclaimer
              hasSurcharge={hasSurcharge}
              tariffSurcharge={tariffSurcharge}
              className="w-100"
            />
          </Stack>
        }
      />
    </AutosuggestPackageRestrictionContext>
  );
};

const AddToCartPackagingSelection = ({
  initialPackCodeResolved,
  selectedPackCode,
  onSelectPackage,
  packagingOptionsState,
}: {
  initialPackCodeResolved: boolean;
  selectedPackCode: string;
  onSelectPackage: (packCode: string) => void;
  packagingOptionsState: ReturnType<typeof usePackagingOptions>;
}) => {
  const theme = useTheme();
  const isDesktopS = useMediaQuery(theme.breakpoints.up("desktop-s"));

  if (!initialPackCodeResolved || packagingOptionsState.status === "loading") {
    return (
      <CenterContents>
        <CircleSpinner />
      </CenterContents>
    );
  }

  if (packagingOptionsState.status !== "loaded") {
    return null;
  }

  return (
    <PackagingSelection
      options={packagingOptionsState.options}
      value={selectedPackCode}
      onChange={onSelectPackage}
      initiallyExpanded={isDesktopS ? "item" : false}
    />
  );
};
